/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbForm } from "abreact/form/AbForm";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbBooleanLogicTreeContext } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeContext";
import { AbBooleanLogicTreeDragDropMachine } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeDragDropMachine";
import { AbGenericFocusManagerContext, useAbGenericFocusManager } from "abreact/incubator/ui/abBooleanLogicTree/AbGenericFocusManager";
import { GroupOfConditionsEditor } from "abreact/incubator/ui/abBooleanLogicTree/GroupOfConditionsEditor";
import { ConditionRowLanguageSource } from "abreact/incubator/ui/abBooleanLogicTree/languageSource/ConditonRowLanguageSource";
import { AbBooleanLogicGroupingContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicGroupingContent";
import { AbBooleanLogicSingleConditionContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicSingleConditionContent";
import { AbBooleanLogicTreeContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeContent";
import { SelectionMachineAction, useAbBooleanLogicTreeSelectionMachine } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeSelectionMachine";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { useOnMount } from "abreact/utils/AbHooks";
import { AbButton, AbButtonType } from "absdk/commons/ui/misc/AbButton";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { EventListenerManager } from "abweb/utils/EventRegistration";
import { useCallback, useState } from "react";
import * as React from "react";

/*
 Terms:
 Group - A sequence of clauses or SubGroups plus an AND/OR decision
 Clause - A single line within a group.  A Clause is a sequence of InputNuggets
 InputNugget - A single (pluggable) input item of a Clause.  (I didn't want to further overload the terms piece block)

Implementation notes:
Andy has a vision of potentially making AbSDK components mobx-free.
In consideration of that, I've made this implementation mobx-free.
The technique I used for state management I used is called immutable.
It's another technique that has a lot of desirable qualities.
Since we already have the immutable-js library in our stack, I used the immutable lists and maps that it provides..
https://immutable-js.github.io/immutable-js/

 */
export let ConditionEvaluation;
/**
 *
 * @author jhoel
 */
export const AbBooleanLogicTree = props => {
  const {
    onChange,
    canDeleteRow,
    canGroupWithNext,
    className,
    readonly,
    dataTestId,
    editorContent,
    customConditionComponent,
    keyGenerator,
    conditionEvaluator,
    dataTestIdGenerator
  } = props;
  const [expressionRef] = useState(() => ReactUtils.createRefHolder());
  const [eventListeners] = useState(new EventListenerManager());
  const [dragDropMachine] = useState(new AbBooleanLogicTreeDragDropMachine());
  const focusManager = useAbGenericFocusManager();
  const selectionMachine = useAbBooleanLogicTreeSelectionMachine({
    keyGenerator: props.keyGenerator,
    treeContent: editorContent,
    onSelectionAction: props.onSelectionAction == null ? undefined : additionalInfo => {
      props.onBeforeUndoableChange();
      props.onSelectionAction(additionalInfo);
      props.onAfterUndoableChange();
    }
  });
  useOnMount(() => {
    if (expressionRef != null && expressionRef.current != null) {
      eventListeners.registerEventListener(expressionRef.current.ownerDocument, "focusin", onFocusChangeHandler, {
        capture: true
      });
      eventListeners.registerEventListener(expressionRef.current.ownerDocument, "mousedown", onMouseDownHandler);
      eventListeners.registerEventListener(expressionRef.current.ownerDocument, "dragleave", onDragLeave);
      dragDropMachine.anchorRef = expressionRef;
    }
    return () => {
      eventListeners.unregister();
    };
  });

  /** Content Change Callbacks **/
  const onAddOuterGroup = useCallback(() => {
    const {
      nestingState
    } = editorContent;
    const newCondition = new AbBooleanLogicSingleConditionContent(keyGenerator());
    const outerGroup = new AbBooleanLogicGroupingContent(keyGenerator(), nestingState.getOppositeConnective()).withAppendSubgroup(nestingState).withAppendSingleRow(newCondition);
    const newState = new AbBooleanLogicTreeContent(outerGroup);
    onChange(newState, {
      newRowKeys: [newCondition.key],
      newGroupKeys: [outerGroup.key]
    });
  }, [editorContent, keyGenerator, onChange]);
  const onGroupChange = useCallback((nestingState, additionalInfo) => {
    // use the immutable technique of building new object compositions
    const immEditorState = new AbBooleanLogicTreeContent(nestingState);
    props.onChange(immEditorState, additionalInfo);
  }, [onChange]);
  const onClearAll = useCallback(() => {
    // Reset to a single, unfilled condition.
    const newKey = props.keyGenerator();
    const immEditorState = props.editorContent.clearAll().appendRowToTopLevel(newKey);
    props.onChange(immEditorState, {
      newRowKeys: [newKey]
    });
  }, [keyGenerator, onChange]);

  /** Click/Drag/Drops Callbacks **/
  const onDragLeave = useCallback(e => {
    if (expressionRef.current?.contains(e.target) === false) {
      dragDropMachine.clearCurrentDropTarget();
    }
  }, [expressionRef]);
  const onDropCondition = useCallback((conditionKey, dataTransfer) => {
    // This currently just handles the move case, not the copy case
    const dropTargetInfo = dragDropMachine.getDropTargetInfo();
    dragDropMachine.clearCurrentDropTarget();
    if (!dropTargetInfo) {
      return;
    }
    const {
      conditionKey: targetConditionKey,
      dropPosition
    } = dropTargetInfo;
    const targetRowOrGroup = props.editorContent.findRowOrGroupLocation(targetConditionKey);
    if (!targetRowOrGroup) {
      return;
    }
    /** adjust target index based on if we are inserting above or below the target **/
    targetRowOrGroup.index = dropPosition === "below" ? targetRowOrGroup.index + 1 : targetRowOrGroup.index;

    /** If we have a selectionMachine, process drop using new selection-based drag drop **/
    if (selectionMachine) {
      selectionMachine.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.DROP,
        dropTargetLocation: targetRowOrGroup,
        dataTransfer: dataTransfer,
        selectionMachine: selectionMachine
      });
    } else {
      /** If we don't have a selection machine, process used old move-single-row method **/
      let newGroupState = props.editorContent.nestingState?.moveRowByKey(conditionKey, targetRowOrGroup);
      if (newGroupState !== props.editorContent.nestingState) {
        props.onBeforeUndoableChange();
        const immEditorState = new AbBooleanLogicTreeContent(newGroupState);
        props.onChange(immEditorState);
        props.onAfterUndoableChange();
      }
    }
  }, [props, selectionMachine, dragDropMachine]);

  // only catch on left click
  const onMouseDownHandler = useCallback(evt => {
    if (evt === undefined || !ClassUtils.isInstanceOf(evt, MouseEvent)) {
      Assert.unexpected("onMouseDownHandler event handler got event that wasn't a MouseEvent");
    }
    if (evt.button !== 0 || !expressionRef || !expressionRef.current) {
      return;
    }
    const event = evt;
    const goingToTarget = event.target;
    if (goingToTarget === null || !ClassUtils.isInstanceOf(goingToTarget, HTMLElement) || !ElementUtils.isAncestorOf(goingToTarget, expressionRef.current)) {
      selectionMachine?.clearCurrentSelection();
    }
    if (evt.shiftKey) {
      // we have distinct shiftKey click behavior for mouseDown, we don't want to allow default text selection
      evt.preventDefault();
    }
  }, [expressionRef, selectionMachine]);
  const onFocusChangeHandler = useCallback(evt => {
    Assert.assert(evt !== undefined, "FocusOut event handler didn't get an event");
    if (!ClassUtils.isInstanceOf(evt, FocusEvent)) {
      Assert.unexpected("FocusOut event handler got event that wasn't a FocusEvent");
    }
    if (!expressionRef || !expressionRef.current) {
      return;
    }
    const event = evt;
    const goingToTarget = event.target;
    if (goingToTarget == null) selectionMachine?.clearCurrentSelection();
    let htmlTarget = event.target;
    const popup = htmlTarget.closest(".ab-action-bar-menu-rcmp");
    // Don't erase focus if clicking on the ab-action-bar-menu popup
    if (popup !== null) {
      return;
    }

    // Drop focus, but not selection, if we tab out of the guided expression editor
    if (!ClassUtils.isInstanceOf(goingToTarget, HTMLElement) || !ElementUtils.isAncestorOf(goingToTarget, expressionRef.current)) {
      selectionMachine?.clearFocus();
    }
  }, [expressionRef, selectionMachine]);

  /** Render Callbacks **/
  const renderAddOuterGroup = useCallback(() => {
    if (readonly) {
      return null;
    }
    if (editorContent.nestingState.sequence.size < 2) {
      return null;
    }
    const oppositeConnective = editorContent.nestingState.getOppositeConnective();
    const label = oppositeConnective === "AND" ? AbReactResources.abBooleanLogicTree_addAndGroup() : AbReactResources.abBooleanLogicTree_addOrGroup();
    return /*#__PURE__*/React.createElement(AbButton, {
      className: "abui add-outer-group-btn",
      type: AbButtonType.LINK,
      style: {
        fontSize: "var(--ab-local-font-size)"
      },
      onClick: () => onAddOuterGroup(),
      label: label
    });
  }, [readonly, editorContent]);
  const rowLanguageSource = props.rowLanguageSource ?? new ConditionRowLanguageSource();
  const dataTestIdToUse = dataTestIdGenerator ? dataTestIdGenerator.generateID(editorContent.nestingState.key) : dataTestId;
  const wrapperClassName = CssUtils.composeClassNames("ab-boolean-logic-tree", className);

  // CSS Class Name is assigned in <GroupOfConditionsEditor>
  return /*#__PURE__*/React.createElement(AbGenericFocusManagerContext.Provider, {
    value: focusManager
  }, /*#__PURE__*/React.createElement(AbBooleanLogicTreeContext.Provider, {
    value: {
      canDeleteRow: canDeleteRow,
      focusManager: focusManager,
      canGroupWithNext: canGroupWithNext,
      dragDropMachine: dragDropMachine,
      selectionMachine: selectionMachine
    }
  }, /*#__PURE__*/React.createElement("div", {
    ref: r => expressionRef.current = r,
    onClick: () => selectionMachine?.clearCurrentSelection()
  }, /*#__PURE__*/React.createElement(AbForm, {
    className: wrapperClassName,
    dataTestId: dataTestIdToUse,
    onSubmit: () => {/* Prevent Default onSubmit behavior */},
    __htmlProps: {
      "aria-label": AbReactResources.abBooleanLogicTree_accessibility_formLabel()
    }
  }, /*#__PURE__*/React.createElement(GroupOfConditionsEditor, {
    readonly: !!readonly,
    onChange: onGroupChange,
    groupEditorState: editorContent.nestingState,
    customConditionComponent: customConditionComponent,
    keyGenerator: keyGenerator,
    onBeforeUndoableChange: props.onBeforeUndoableChange,
    onAfterUndoableChange: props.onAfterUndoableChange,
    onDropCondition: onDropCondition.bind(this),
    onClearAll: onClearAll,
    conditionEvaluator: conditionEvaluator,
    dragDropMachine: dragDropMachine,
    selectionMachine: selectionMachine,
    compoundConditionGroupIndex: "",
    dataTestIdGenerator: props.dataTestIdGenerator,
    isGroupDisabled: false,
    nestedUnderDisabled: false,
    serverSupportsDisabledConditionSection: props.serverSupportsDisabledConditionSection,
    supportsSubgroups: props.supportsSubgroups,
    rowLanguageSource: rowLanguageSource
  }), props.supportsSubgroups && renderAddOuterGroup()))));
};
//# sourceMappingURL=AbBooleanLogicTree.js.map
